import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { TABLET_BREAKPOINT } from "@monorepo/const";
import { useResize } from "@monorepo/hooks";

import { ModalContext } from "../context";
import { setModalController } from "../controller";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { Root } from "../root";
import {
    DEFAULT_BORDER_RADIUS,
    DEFAULT_CLOSE_ON_BACKDROP,
    DEFAULT_CLOSE_ON_ESCAPE,
    DEFAULT_CONTAINER_ID,
    DEFAULT_PADDING_DESKTOP,
    DEFAULT_PADDING_MOBILE,
    DEFAULT_SHOW_CLOSE_BUTTON,
} from "../model/const";
import { generateModalId, isBrowser } from "../model/utils";

import type { ModalEntry, ModalHandle, ModalOpenOptions, ModalProviderProps, ModalRender } from "../model/types";

export const ModalProvider = ({ children, disableBodyScroll = true, containerId }: ModalProviderProps) => {
    const [modals, setModals] = useState<ModalEntry[]>([]);
    const [container, setContainer] = useState<HTMLElement | null>(null);
    const width = useResize();
    const isMobile = width < TABLET_BREAKPOINT;
    const defaultPadding = isMobile ? DEFAULT_PADDING_MOBILE : DEFAULT_PADDING_DESKTOP;

    const originalOverflowRef = useRef("");
    const originalPaddingRightRef = useRef("");

    useEffect(() => {
        if (!isBrowser()) {
            return undefined;
        }

        const targetId = containerId || DEFAULT_CONTAINER_ID;
        let element = targetId ? document.getElementById(targetId) : null;
        let created = false;

        if (!element) {
            element = document.createElement("div");

            if (targetId) {
                element.id = targetId;
            }

            document.body.appendChild(element);
            created = true;
        }

        setContainer(element);

        return () => {
            setContainer(null);

            if (created && element?.parentNode) {
                element.parentNode.removeChild(element);
            }
        };
    }, [containerId]);

    useBodyScrollLock(modals.length > 0, !disableBodyScroll, originalOverflowRef, originalPaddingRightRef);

    const close = useCallback((id: string) => {
        let closingModal: ModalEntry | undefined;

        setModals((prev) => {
            const next = prev.filter((modal) => {
                if (modal.id === id) {
                    closingModal = modal;
                    return false;
                }

                return true;
            });

            return next;
        });

        closingModal?.options.onClose?.();
    }, []);

    const closeAll = useCallback(() => {
        let closingModals: ModalEntry[] = [];

        setModals((prev) => {
            closingModals = prev;
            return [];
        });

        closingModals.forEach((modal) => {
            modal.options.onClose?.();
        });
    }, []);

    const open = useCallback(
        (render: ModalRender, options?: ModalOpenOptions): ModalHandle => {
            const id = options?.id ?? generateModalId();

            const normalizedOptions: ModalEntry["options"] = {
                closeOnBackdrop: options?.closeOnBackdrop ?? DEFAULT_CLOSE_ON_BACKDROP,
                closeOnEscape: options?.closeOnEscape ?? DEFAULT_CLOSE_ON_ESCAPE,
                borderRadius: `${options?.borderRadius ?? DEFAULT_BORDER_RADIUS}px`,
                padding: options?.padding ?? defaultPadding,
                className: options?.className,
                backdropClassName: options?.backdropClassName,
                showCloseButton: options?.showCloseButton ?? DEFAULT_SHOW_CLOSE_BUTTON,
                onClose: options?.onClose,
                onOpen: options?.onOpen,
            };

            setModals((prev) => {
                const filtered = prev.filter((modal) => modal.id !== id);

                return [
                    ...filtered,
                    {
                        id,
                        render,
                        options: normalizedOptions,
                    },
                ];
            });

            normalizedOptions.onOpen?.();

            return {
                id,
                close: () => close(id),
            };
        },
        [close]
    );

    useEffect(() => {
        if (!isBrowser()) {
            return undefined;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") {
                return;
            }

            if (!modals.length) {
                return;
            }

            const lastModal = modals[modals.length - 1];

            if (!lastModal.options.closeOnEscape) {
                return;
            }

            close(lastModal.id);
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [close, modals]);

    const value = useMemo(
        () => ({
            modals,
            container,
            open,
            close,
            closeAll,
        }),
        [close, closeAll, container, modals, open]
    );

    useEffect(() => {
        setModalController({
            open,
            close,
            closeAll,
        });

        return () => {
            setModalController(null);
        };
    }, [close, closeAll, open]);

    return (
        <ModalContext.Provider value={value}>
            {children}
            <Root />
        </ModalContext.Provider>
    );
};
